import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface ProductService {
  product_n_service_id: number;
  product_n_service_name: string;
  product_n_service_description: string;
  product_n_service_unit_price: number;
  currency_code: string;
  created_datetime: string;
  updated_datetime: string;
  is_active: boolean;
  tenant_id: number;
  created_by: number | null;
  updated_by: number | null;
  product_n_service_type_id: number;
  product_n_service_type?: ProductServiceType;
}

export interface ProductServiceType {
  product_n_service_type_id: number;
  product_n_service_type_display_name: string;
  product_n_service_type_key: string;
}

export interface ProductServiceRequest {
  product_n_service_name: string;
  product_n_service_description?: string;
  product_n_service_unit_price: number;
  currency_code: string;
  is_active: boolean;
  product_n_service_type_id: number;
  tenant_id?: number;
}

export interface PaginatedProductServiceResponse {
  results: ProductService[];
  count: number;
  next: string | null;
  previous: string | null;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  pageSize: number;
}

const addTenantToProductService = <T extends { tenant_id?: number | null }>(
  productService: T,
  tenantId: string | number
): T => {
  if (!tenantId) {
    throw new Error("Tenant ID not provided.");
  }

  return {
    ...productService,
    tenant_id: Number(tenantId),
  };
};

export const ProductServiceService = {
  getProductServicesByPage: async (
    tenantId: string | number,
    page: number = 1,
    pageSize: number = 20,
    filters?: Record<string, any>
  ): Promise<PaginatedProductServiceResponse> => {
    try {
      if (!tenantId) throw new Error("Tenant ID not provided.");

      const params: Record<string, any> = {
        page: page,
        page_size: pageSize,
        tenant_id: tenantId,
      };

      if (filters) {
        if (filters.searchTerm) {
          params.product_n_service_name = filters.searchTerm;
        }
        if (filters.typeFilter) {
          params.product_n_service_type = filters.typeFilter;
        }
        if (filters.dateRange && filters.dateRange[0]) {
          params.created_datetime_gte = filters.dateRange[0]
            .toISOString()
            .split("T")[0];
        }
        if (filters.dateRange && filters.dateRange[1]) {
          params.created_datetime_lte = filters.dateRange[1]
            .toISOString()
            .split("T")[0];
        }
      }
      const response = await axios.get(`${API_BASE_URL}/product-n-service/`, {
        headers: getAuthHeaders(),
        params: params,
      });

      let productServices: ProductService[] = [];
      let totalCount = 0;

      if (response.data.results) {
        productServices = response.data.results;
        totalCount = response.data.count || productServices.length;
      } else if (Array.isArray(response.data)) {
        productServices = response.data;
        totalCount = productServices.length;
      } else {
        console.warn("Unexpected API response format:", response.data);
        return {
          results: [],
          count: 0,
          next: null,
          previous: null,
          totalPages: 0,
          currentPage: page,
          hasMore: false,
          pageSize: pageSize,
        };
      }

      const totalPages = Math.ceil(totalCount / pageSize);
      const hasMore = page < totalPages;

      return {
        results: productServices,
        count: totalCount,
        next: response.data.next,
        previous: response.data.previous,
        totalPages,
        currentPage: page,
        hasMore,
        pageSize: pageSize,
      };
    } catch (error) {
      console.error("Error fetching product services page:", error);
      return handleAuthError(error);
    }
  },

  getAllProductServices: async (tenantId: string | number): Promise<ProductService[]> => {
    try {
      const response = await ProductServiceService.getProductServicesByPage(tenantId, 1, 1000);
      return response.results;
    } catch (error) {
      console.error("Error fetching product services:", error);
      return handleAuthError(error);
    }
  },

  getProductServiceById: async (id: number): Promise<ProductService> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/product-n-service/${id}/`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching product service:", error);
      return handleAuthError(error);
    }
  },

  createProductService: async (productService: Partial<ProductServiceRequest>, tenantId: string | number): Promise<ProductService> => {
    try {
      const productServiceWithTenant = addTenantToProductService(productService, tenantId);
      const response = await axios.post(
        `${API_BASE_URL}/product-n-service/`,
        productServiceWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating product service:", error);
      return handleAuthError(error);
    }
  },

  updateProductService: async (id: number, productService: Partial<ProductServiceRequest>, tenantId: string | number): Promise<ProductService> => {
    try {
      const productServiceWithTenant = addTenantToProductService(productService, tenantId);
      const response = await axios.patch(
        `${API_BASE_URL}/product-n-service/${id}/`,
        productServiceWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating product service:", error);
      return handleAuthError(error);
    }
  },

  deleteProductService: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_BASE_URL}/product-n-service/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting product service:", error);
      return handleAuthError(error);
    }
  },

  getProductServiceTypes: async (): Promise<ProductServiceType[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/product-n-service-type/`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching product service types:", error);
      return handleAuthError(error);
    }
  },
};
